
import Themeable from 'vuetify/lib/mixins/themeable';
import { Vue, Component } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import UsersRepo from '@/services/Users';
import ProfileRepository from '@/services/Users-profile';
import { UserShiftInterface } from '@/services/Users/Interfaces';
import DepartmentRepository from '@/services/Departments';
import ShiftRepository from '@/services/Calendar/Shifts';
import ShiftTypeRepository from '@/services/Calendar/ShiftType';
import { ShiftTypeInterface } from '@/services/Calendar/ShiftType/Interfaces';
import ShiftTeamRepository from '@/services/Calendar/ShiftTeam';
import { DepartmentInterface } from '@/services/Departments/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { CreateShiftForm } from '@/services/Calendar/Shifts/forms/CreateShiftForm';
import {
  ShiftInterface,
  ShiftStatisticMapInterface,
  ShiftStatisticsWSInterface,
} from '@/services/Calendar/Shifts/Interfaces';
import { ShiftTeamUserWithShiftsInterface, ShiftTeamUserType } from '@/services/Calendar/ShiftTeam/Interfaces';
import { EventType, EventsInterface } from '@/services/Calendar/Events/Interfaces';
import EventsRepository from '@/services/Calendar/Events';
import ShiftAutomationRepository from "@/services/Calendar/ShiftAutomation";
import { Roles } from "@/services/Roles";
import PageHeader from '@/components/layout/PageHeader.vue';

interface ShiftDialogData {
  active: boolean
  loading: boolean
  shiftId: number
  isShiftLeader: boolean
  applySameShiftsToTeam: boolean
  btnSave: boolean
  btnDelete: boolean
  statistic?: ShiftStatisticMapInterface
}

@Component({
  mixins: [Themeable],
  components: {
    PageHeader,
  },
  inject: [
    'userRepository',
    'profileRepository',
    'departmentRepository',
    'shiftRepository',
    'shiftTeamRepository',
    'shiftTypeRepository',
    'eventsRepository',
    'shiftAutomationRepository',
  ],
  sockets: {
    async shiftStatisticData(msg: ShiftStatisticsWSInterface): Promise<void> {
      // @ts-ignore
      this.handleShiftStatisticData(msg);
    },
  },
})
export default class Shifts extends Vue {
  private readonly userRepository!: UsersRepo;
  private readonly profileRepository!: ProfileRepository;
  private readonly departmentRepository!: DepartmentRepository;
  private readonly shiftRepository!: ShiftRepository;
  private readonly shiftTeamRepository!: ShiftTeamRepository;
  private readonly shiftTypeRepository!: ShiftTypeRepository;
  private readonly eventsRepository!: EventsRepository;
  private readonly shiftAutomationRepository!: ShiftAutomationRepository;

  private loading: boolean = true;

  private createAutoDialog = {
    active: false,
    userId: '' as string,
    shiftTypeId: null as number | null,
    loading: false,
  };

  private shiftDialogData: ShiftDialogData = {
    active: false,
    loading: false,
    shiftId: 0,
    isShiftLeader: false,
    applySameShiftsToTeam: true,
    btnSave: false,
    btnDelete: false,
  };

  private createScheduleDialog = {
    active: false,
    loading: false,
    userId: '' as string,
    sequence: '' as string,
    startDate: '' as string, // use ISO yyyy-mm-dd from v-date-picker
    endDate: '' as string,
    mStart: false,
    mEnd: false,
  };
  private headers: DataTableHeader[] = [];
  private menuDate: boolean = false;
  private date: string = new Date().toISOString().substr(0, 7);
  private selectedYear: number = Number(this.date.substr(0, 4));
  private selectedMonth: number = Number(this.date.substr(5, 2));
  private daysOfTheMonth: number = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
  private holidays: Map<number, EventsInterface> = new Map();
  private shiftTypes: ShiftTypeInterface[] = [];
  private selectedDepartment: string = this.$store.getters.departmentId;
  private shiftLeaders: Map<number, ShiftTeamUserWithShiftsInterface[]> = new Map();
  private unsortedUsers: UserShiftInterface[] = [];
  private departments: DepartmentInterface[] = [];
  private form: WPXFormValue<CreateShiftForm> = new WPXFormValue(CreateShiftForm);
  private userStatistics: Map<string, ShiftStatisticMapInterface> = new Map();
  private shiftTableCounter: number = 0;

  private rules = {
    required: (v: any) => (!!v || v === 0) || 'Required',
    // UPPER alphanum tokens or OFF, dash-delimited
    sequence: (v: string) => /^(?:[A-Z0-9]+|OFF)(?:-(?:[A-Z0-9]+|OFF))*$/.test((v||'').trim()) || 'Use tokens like D,N,MRN,OFF joined by "-"',
    range: (start: string, end: string) => {
      if (!start || !end) return true;
      return new Date(end) >= new Date(start) || 'End date must be after start date';
    },
  };

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private get canManageShifts(): boolean {
    return this.userHasScopes(Roles.CALENDAR_SHIFTS_CREATE);
  }

  get canSubmitSchedule(): boolean {
    const d = this.createScheduleDialog;
    return !!d.userId
      && !!d.sequence
      && this.rules.sequence(d.sequence) === true
      && !!d.startDate
      && !!d.endDate
      && this.rules.range(d.startDate, d.endDate) === true
      && !d.loading;
  }

  private openCreateSchedule(userId: string) {
    this.createScheduleDialog = {
      active: true,
      loading: false,
      userId,
      sequence: 'D-D-N-N-OFF-OFF-OFF-OFF',
      startDate: `${this.selectedYear}-${('0'+this.selectedMonth).slice(-2)}-01`,
      endDate: `${this.selectedYear}-${('0'+this.selectedMonth).slice(-2)}-${('0'+this.daysOfTheMonth).slice(-2)}`,
      mStart: false,
      mEnd: false,
    };
  }

  private async confirmCreateSchedule() {
    const d = this.createScheduleDialog;
    if (!this.canSubmitSchedule) return;

    d.loading = true;
    try {
      await this.shiftRepository.createShiftSchedule({
        userId: d.userId,
        startDate: new Date(d.startDate),
        endDate: new Date(d.endDate),
        sequence: d.sequence.trim(),
      });

      this.$successDialog.show('Schedule created.');
      d.active = false;
      // refresh data
      await this.initializeUsersFromDepartment(this.selectedDepartment);
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || 'Could not create schedule.');
    } finally {
      d.loading = false;
    }
  }

  private async deleteSchedule(userId: string): Promise<void> {
    const ok = window.confirm(
      `Delete all shifts for this user in ${this.selectedMonth}.${this.selectedYear}?`
    );
    if (!ok) {
      return;
    }

    try {
      const startDate = new Date(this.selectedYear, this.selectedMonth - 1, 1);
      const endDate = new Date(this.selectedYear, this.selectedMonth - 1, this.daysOfTheMonth);

      await this.shiftRepository.deleteShiftSchedule(userId, startDate, endDate);

      this.$successDialog.show('Schedule deleted.');
      await this.initializeUsersFromDepartment(this.selectedDepartment);
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || 'Could not delete schedule.');
    }
  }

  private async openCreateAutomation(userId: string) {
    try {
      const list = await this.shiftAutomationRepository.getShiftAutomationsForUser(userId);
      const hasOne = Array.isArray(list) ? list.length > 0 : !!list;
      if (hasOne) {
        this.$errorDialog.show('User already has an automation. Delete it first.');
        return;
      }
      this.createAutoDialog.userId = userId;
      this.createAutoDialog.shiftTypeId = this.shiftTypes[0]?.id ?? null;
      this.createAutoDialog.active = true;
    } catch {
      this.$errorDialog.show('Could not check automation.');
    }
  }

  private async confirmCreateAutomation() {
    this.createAutoDialog.loading = true;
    try {
      await this.shiftAutomationRepository.createShiftAutomation({
        userId: this.createAutoDialog.userId,
        shiftType: this.createAutoDialog.shiftTypeId!,
      });
      this.$successDialog.show('Automation created.');
      this.createAutoDialog.active = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || 'Could not create automation.');
    } finally {
      this.createAutoDialog.loading = false;
    }
  }


  private async showAutomation(userId: string) {
    const data = await this.shiftAutomationRepository.getShiftAutomationsForUser(userId);
    this.$infoDialog.show(JSON.stringify(data, null, 2));
  }

  private async deleteAutomation(userId: string) {
    try {
      const data = await this.shiftAutomationRepository.getShiftAutomationsForUser(userId);
      if (data && data.length > 0) {
        await this.shiftAutomationRepository.deleteShiftAutomation(data[0].id);
        this.$successDialog.show('Automation deleted.');
      } else {
        this.$infoDialog.show('User does not have an automation.');
      }
    } catch {
      this.$errorDialog.show('Could not delete automation.');
    }
  }

  private async approveOrUnapproveCurrentMonth(isApproved: boolean): Promise<void> {
    const start = new Date(Date.UTC(this.selectedYear, this.selectedMonth - 1, 1));
    const end = new Date(Date.UTC(this.selectedYear, this.selectedMonth, 0));

    try {
      await this.shiftRepository.approveShifts(start, end, isApproved, this.selectedDepartment);

      this.$successDialog.show(isApproved
        ? 'Schedule approved for this month.'
        : 'Schedule unapproved for this month.');

      await this.initializeUsersFromDepartment(this.selectedDepartment);
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || 'Could not update approval status.');
    }
  }



  private exportExcel(): void {
    const rowsData: Array<{
      fullName: string,
      shifts: Record<string, any>,
      stats: {
        swh: number,
        awh: string,
        ot: string,
        ot3: string,
        otty: string
      }
    }> = [];

    const getStats = (userId: string) => {
      const st = this.userStatistics.get(userId)!;
      return {
        swh: st.currentMonth.standardWorkingHours,
        awh: Number(st.currentMonth.actualWorkingHours).toFixed(2),
        ot: Number(st.currentMonth.overtime).toFixed(2),
        ot3: Number(st.currentQuarter.overtime).toFixed(2),
        otty: Number(st.year.overtime).toFixed(2),
      };
    };


    this.shiftLeaders.forEach(teamUsers => {
      teamUsers.forEach(u => {
        rowsData.push({
          fullName: `${u?.details?.firstName} ${u?.details?.lastName}`,
          shifts: u.shifts,
          stats: getStats(u.userId)
        });
      });
    });

    this.unsortedUsers.forEach(u => {
      rowsData.push({
        fullName: `${u.details.firstName} ${u.details.lastName}`,
        shifts: u.shifts,
        stats: getStats(u.id)
      });
    });

    const thead = `
    <tr>
      ${this.headers.map(h => `<th>${h.value}</th>`).join('')}
    </tr>`;

    const tbodyRows = rowsData.map(r => {
      return `
      <tr>
        ${this.headers.map(h => {
        if (h.value === 'name') {
          return `<td>${r.fullName}</td>`;
        }

        if (['swh','awh','ot','ot3','otty'].includes(h.value)) {
          return `<td style="text-align:center">${r.stats[h.value as keyof typeof r.stats]}</td>`;
        }

        const shift = r.shifts[h.value];
        if (shift) {
          const shortName = shift.shiftType.shortName;
          const ot = shift.overtime > 0 ? ` +${shift.overtime}` : '';
          return `<td style="text-align:center">${shortName}${ot}</td>`;
        }
        return `<td style="text-align:center">OFF</td>`;
      }).join('')}
      </tr>`;
    }).join('');

    const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Shifts ${this.date}</x:Name>
              <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
    </head>
    <body>
      <table border="1">
        <thead>${thead}</thead>
        <tbody>${tbodyRows}</tbody>
      </table>
    </body>
    </html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Shifts-${this.selectedDepartment}-${this.selectedYear}-${('0'+this.selectedMonth).slice(-2)}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public getHeaders(): void {
    const headers: DataTableHeader[] = [
      { text: 'Name', value: 'name', sortable: false },
    ];

    for (let i = 1; i <= this.daysOfTheMonth; i++) {
      const dayOfWeek = this.dayOfWeek(i);
      const holiday = this.holidays.get(i);
      let typeOfDay = 'rest';

      if (dayOfWeek !== 'Saturday' && dayOfWeek !== 'Sunday') {
        typeOfDay = 'work';
      }

      if (holiday) {
        typeOfDay = 'holiday';

        if (dayOfWeek === 'Saturday' || dayOfWeek === 'Sunday') {
          typeOfDay += ' weekend';
        }
      }

      headers.push({
        text: String(i),
        value: `${this.selectedYear}-${('0' + this.selectedMonth).slice(-2)}-${('0' + i).slice(-2)}`,
        sortable: false,
        class: typeOfDay + ' px-0',
      });
    }

    headers.push(
      { text: 'Standard Working Hours', value: 'swh', sortable: false, class: ' px-0' },
      { text: 'Actual Working Hours', value: 'awh', sortable: false, class: ' px-0' },
      { text: 'Overtime', value: 'ot', sortable: false, class: ' px-0' },
      { text: 'Overtime For The Trimester', value: 'ot3', sortable: false, class: ' px-0' },
      { text: 'Overtime For The Year ', value: 'otty', sortable: false, class: ' px-0' },
    );

    this.headers = headers;
  }

  public async mounted(): Promise<void> {
    this.holidays = new Map();
    const holidays = await this.eventsRepository.getEventsForMonth(this.selectedYear, this.selectedMonth);
    holidays.forEach((holiday) => {
      if (Number(holiday.type) === EventType.national) {
        holiday.date = new Date(holiday.date);
        this.holidays.set(holiday.date.getDate(), holiday);
      }
    });

    this.getHeaders();
    this.shiftTypes = await this.shiftTypeRepository.getAllShiftTypes();

    await this.initializeDepartments();
    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private dayOfWeek(day: number): string {
    const date = new Date(`${this.selectedYear}-${this.selectedMonth}-${day}`);
    const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return weekday[date.getDay()];
  }

  private getHolidayInfo(day: number): string {
    const holiday = this.holidays.get(day);
    const dayOfWeek = this.dayOfWeek(day);

    if (holiday) {
      return `<b>${holiday.name}</b><br />${dayOfWeek}`;
    }

    return dayOfWeek;
  }

  private async initializeDepartments(): Promise<void> {
    try {
      const departments = await this.departmentRepository.getAllDepartments();

      departments.map((department: DepartmentInterface) => {
        this.departments.push(department);
      });
    } catch (err) {
      this.$errorDialog.show('Could not retrieve departments.');
    }
  }

  private async initializeUsersFromDepartment(departmentId: string): Promise<void> {
    try {
      const users: UserShiftInterface[] = await this.userRepository.getUsersByDepartment(departmentId);
      const teams = await this.shiftTeamRepository.getAllShiftTeams(departmentId, new Date(this.date));
      const defaultStatistics = { standardWorkingHours: 0, actualWorkingHours: 0, overtime: 0 }
      this.shiftLeaders = new Map();
      this.unsortedUsers = [];

      const usersIds = users.map((user) => user.id);
      const usersProfiles = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
      const usersShifts = await this.shiftRepository.getShiftsByUsersIds(usersIds, new Date(this.selectedYear, this.selectedMonth));
      for (const user of users) {
        const i = users.indexOf(user);
        const shifts = usersShifts.filter((shifts) => user.id === shifts.id)[0].shifts
        user.shifts = await this.getUserShifts(shifts);
        user.details = usersProfiles.filter((profile) => user.id === profile.userId)[0];
        this.shiftTeamRepository.getUserShiftStatistics(user.id, new Date(this.selectedYear, this.selectedMonth));
        users[i] = user;
        this.userStatistics.set(user.id, {
          currentMonth: defaultStatistics,
          currentQuarter: defaultStatistics,
          year: defaultStatistics,
        });
      }

      teams.forEach((team) => {
        team.shiftTeamUsers
          .sort((teamUser) => teamUser.userType === ShiftTeamUserType.SHIFT_LEADER ? 0 : 1)
          .forEach(async (teamUser) => {
            const userData = users.find((user) => user.id === teamUser.userId);
            const existingTeam = this.shiftLeaders.get(team.id);

            if (typeof existingTeam === 'undefined') {
              this.shiftLeaders.set(team.id, [{
                ...teamUser,
                details: userData?.details,
                shifts: userData?.shifts,
              }] as ShiftTeamUserWithShiftsInterface[]);
            } else {
              existingTeam.push({
                ...teamUser,
                details: userData?.details,
                shifts: userData?.shifts,
              } as ShiftTeamUserWithShiftsInterface)

              this.shiftLeaders.set(team.id, existingTeam);
            }

            users.splice(users.findIndex((user) => user.id === teamUser.userId), 1);
          });
      });

      this.unsortedUsers = users;
    } catch (err) {
      this.$errorDialog.show('Could not retrieve users.');
      console.error(err);
    }
  }

  private async getUserShifts(userShifts: ShiftInterface[]): Promise<any> {
    const shifts: { [key: string]: ShiftInterface }  = {};
    userShifts.map((shift) => {
      shifts[(new Date(shift.date)).toISOString().slice(0, 10)] = shift;
    });

    return shifts;
  }

  private async updateSelectedDepartment(val: string): Promise<void> {
    this.loading = true;
    this.selectedDepartment = val;

    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private async updateSelectedDate(val: string): Promise<void> {
    this.loading = true;
    this.date = val;
    this.selectedYear = Number(this.date.substr(0, 4));
    this.selectedMonth = Number(this.date.substr(5, 2));
    this.daysOfTheMonth = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    this.holidays = new Map();
    const holidays = await this.eventsRepository.getEventsForMonth(this.selectedYear, this.selectedMonth);
    holidays.forEach((holiday) => {
      if (Number(holiday.type) === EventType.national) {
        holiday.date = new Date(holiday.date);
        this.holidays.set(holiday.date.getDate(), holiday);
      }
    });

    this.getHeaders();

    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private openShiftDialog(id: string, date: string, isShiftLeader: boolean = false): void {
    this.form.data.userId = id;
    this.form.data.shiftType = this.shiftTypes[0].id;
    this.form.data.date = date;
    this.form.data.overtime = 0;
    this.shiftDialogData.isShiftLeader = isShiftLeader;
    this.shiftDialogData.active = true;
    this.shiftDialogData.statistic = this.userStatistics.get(id);
  }

  private async saveShift(): Promise<void> {
    this.shiftDialogData.btnSave = true;

    try {
      await this.shiftRepository.createShift({
        userId: this.form.data.userId,
        shiftType: this.form.data.shiftType,
        overtime: Number(this.form.data.overtime),
        date: new Date(this.form.data.date),
        applySameShiftsToTeam: Boolean(this.shiftDialogData.applySameShiftsToTeam),
      });
    } catch (e) {
      this.$errorDialog.show(e.message);
    }

    await this.closeShiftDialog();
  }

  private async deleteShift(): Promise<void> {
    this.shiftDialogData.btnDelete = true;

    try {
      await this.shiftRepository.deleteShift(
        this.form.data.userId,
        new Date(this.form.data.date),
        this.shiftDialogData.applySameShiftsToTeam,
      );
    } catch (e) {
      this.$errorDialog.show('A problem occurred while deleting the shift');
    }

    await this.closeShiftDialog();
  }

  private async closeShiftDialog(): Promise<void> {
    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.shiftDialogData.btnSave = false;
    this.shiftDialogData.btnDelete = false;
    this.shiftDialogData.active = false;
    this.shiftDialogData.statistic = undefined;
  }

  private async handleShiftStatisticData(statistics: ShiftStatisticsWSInterface): Promise<void>  {
    await this.sleep(50);
    this.userStatistics.set(statistics.userId, {
      currentMonth: statistics.currentMonth,
      currentQuarter: statistics.currentQuarter,
      year: statistics.year,
    });
    this.shiftTableCounter += 1;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
